/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ШАБЛОН БЕЙДЖА текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Один документ на мероприятие, а не список: бейдж спортсмена отличается от
 * бейджа прессы РОЛЬЮ и НАБОРОМ ЗОН, и то и другое рисуется из данных
 * участника элементами `ROLE` и `ZONES`. Второй макет не добавил бы ни одного
 * сведения, зато добавил бы вопрос «а этот человек по какому печатается».
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ «ШАБЛОНА НЕТ» — ЭТО СОСТОЯНИЕ, А НЕ ОШИБКА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * У нового мероприятия шаблона нет по определению, и сервер отвечает на это
 * 404. Положить такой ответ в `error` значило бы показывать красный тост и
 * кнопку «Повторить» человеку, который просто ещё не выбрал заготовку.
 *
 * Поэтому здесь ТРИ различимых состояния, а не два:
 *
 *     ready && template !== null   макет есть;
 *     ready && template === null   макета нет — показать галерею заготовок;
 *     error !== null               не удалось узнать — показать ошибку.
 *
 * Их нельзя схлопывать: `template === null` без `ready` означает «ещё не
 * загрузили», и галерея заготовок, показанная в этот момент, мигает на каждом
 * заходе в раздел.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЧЕРНОВИК ЖИВЁТ В РЕДАКТОРЕ, А НЕ ЗДЕСЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Стор хранит СОХРАНЁННЫЙ шаблон — ровно то, что сейчас лежит на сервере, и
 * ровно то, что напечатается. Несохранённые правки (перетаскивания, история
 * отмен, выделение) принадлежат экрану конструктора и в стор не попадают.
 *
 * Смешать их — самая дорогая ошибка этого экрана: печать берёт данные отсюда,
 * и черновик в сторе означал бы, что оператор печатает макет, которого никто
 * не сохранял, и которого не увидит никто другой.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  КАРТИНКИ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Подложки и логотипы лежат за эндпоинтом, который требует `Authorization`,
 * поэтому `<img src>` на него навести нельзя. `assetUrl()` забирает байты
 * axios'ом и отдаёт **data-URL**, кэшируя их по ключу.
 *
 * ⚠️ Именно data-URL, а не object URL, и это не вкусовщина: те же самые байты
 * уезжают в документ печати, который обязан быть самодостаточным — `blob:`
 * из другого документа Safari в задании печати регулярно не рисует. Один
 * формат на превью и на печать означает, что превью проверяет ровно то, что
 * напечатается.
 *
 * Кэш живёт до смены мероприятия. Ключ содержит свежий uuid на каждую
 * загрузку, так что байты за ключом неизменяемы и устареть не могут.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { badgeTemplateApi } from '@/api/badge-template'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { BadgeAsset, BadgeTemplate, SetBadgeTemplateRequest } from '@/types/api'

export const useBadgeTemplateStore = defineStore('badgeTemplate', () => {
  /* ───────────────────────── state ───────────────────────── */

  const eventId = ref<string | null>(null)

  /** Сохранённый шаблон. `null` — его нет (см. шапку), а не «не загрузился». */
  const template = shallowRef<BadgeTemplate | null>(null)

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)
  const ready = ref(false)

  /**
   * Кэш картинок: ключ объекта → data-URL.
   *
   * `shallowRef` с обычным `Map` внутри: реактивность нужна на уровне «карта
   * поменялась», а не на уровне каждого значения, и глубокий прокси над
   * тысячей символов base64 — это заметная работа на ровном месте.
   */
  const assetUrls = shallowRef(new Map<string, string>())

  /* ───────────────────────── getters ─────────────────────── */

  /**
   * Шаблон есть и его можно печатать.
   *
   * ⚠️ `ready` в условии обязателен: до загрузки `template === null` означает
   * «ещё не знаем», и кнопка печати, погасшая на полсекунды при каждом входе в
   * раздел, читается как «печать сломалась».
   */
  const hasTemplate = computed(() => ready.value && template.value !== null)

  /** Макета нет — экран показывает галерею заготовок. */
  const isEmpty = computed(() => ready.value && template.value === null && error.value === null)

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    if (eventId.value !== id) {
      template.value = null
      ready.value = false
      // Ключи картинок скоупятся мероприятием, так что чужой кэш не просто
      // бесполезен — он держит в памяти чужие мегабайты.
      assetUrls.value = new Map()
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await badgeTemplateApi.get(id, ctl.signal)
      // ⚠️ Проверка стоит и здесь, и в `catch`: HTTP не обещает порядок
      // ответов, поэтому ответ может прийти уже после `abort()`, но раньше,
      // чем тот дойдёт до сокета.
      if (controller !== ctl) return
      template.value = data
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      template.value = null
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /** Бросают `ApiError`; вызывающий заворачивает в `useAsyncAction`. */

  /**
   * Записать макет целиком.
   *
   * В стор кладётся ОТВЕТ, а не то, что отправили: сервер нормализует документ
   * (подставляет умолчания, опускает регистр цветов), и оставив здесь
   * отправленное, редактор показывал бы одно, а печаталось бы другое.
   *
   * Перечитывания списка после мутации здесь нет — в отличие от справочников:
   * ответ и есть полное новое состояние единственного объекта, и лишний GET
   * означал бы лишнюю секунду на кнопке «Сохранить» ради тех же байтов.
   */
  async function save(input: SetBadgeTemplateRequest): Promise<BadgeTemplate> {
    const id = requireEventId()
    const saved = await badgeTemplateApi.set(id, input)
    template.value = saved
    return saved
  }

  /**
   * Принять макет, записанный ДРУГИМ эндпоинтом.
   *
   * Ровно один вызывающий: применение сохранённого шаблона
   * (`POST .../badge-template/apply`), которое пишет ту же самую строку, но
   * живёт в своём сторе, потому что библиотека глобальна.
   *
   * ⚠️ ЭТО НЕ «ПОЛОЖИТЬ ЧЕРНОВИК В СТОР». Аргумент обязан быть тем, что вернул
   * СЕРВЕР: он нормализован, его картинки лежат под ключами ЭТОГО мероприятия,
   * а ссылки на дополнительные поля переотображены. Собранный на клиенте
   * документ здесь означал бы печать по ключам, которых у мероприятия нет.
   *
   * Действие, а не присваивание снаружи: стор, в который пишут мимо его
   * методов, перестаёт быть единственным местом, где видно, откуда взялось
   * то, что печатается.
   */
  function adopt(next: BadgeTemplate): void {
    template.value = next
  }

  /** Удалить макет. Картинки в хранилище при этом остаются — см. api-модуль. */
  async function remove(): Promise<BadgeTemplate> {
    const id = requireEventId()
    const deleted = await badgeTemplateApi.remove(id)
    template.value = null
    return deleted
  }

  /** Загрузить подложку или логотип и сразу положить его в кэш картинок. */
  async function uploadAsset(file: File): Promise<BadgeAsset> {
    const id = requireEventId()
    const asset = await badgeTemplateApi.uploadAsset(id, file)

    // Байты у нас уже в руках — читать их обратно с сервера ради превью
    // означало бы round trip за тем, что лежит в переменной.
    const next = new Map(assetUrls.value)
    next.set(asset.key, await blobToDataUrl(file, asset.contentType))
    assetUrls.value = next

    return asset
  }

  /* ───────────────────────── картинки ────────────────────── */

  /**
   * data-URL картинки по её ключу или по внешнему `https://`.
   *
   * Внешняя ссылка отдаётся как есть: байтов у нас нет, и качать чужой сервер
   * через себя мы не будем. ⚠️ Следствие, о котором обязан знать вызывающий:
   * в документе печати такая картинка остаётся ССЫЛКОЙ, то есть требует сети в
   * момент печати. Для подложки это плохой выбор, и интерфейс говорит об этом
   * вслух, а не решает за человека.
   *
   * Ошибку загрузки НЕ бросает: бейдж без подложки печатается, бейдж с
   * исключением посреди отрисовки — нет. Вызывающий видит `null` и рисует
   * пустое место; отдельный признак «не загрузилось» экран показывает рядом.
   */
  async function assetUrl(key: string): Promise<string | null> {
    const trimmed = key.trim()
    if (!trimmed) return null
    if (trimmed.includes('://')) return trimmed

    const cached = assetUrls.value.get(trimmed)
    if (cached) return cached

    const id = eventId.value
    if (!id) return null

    try {
      const blob = await badgeTemplateApi.fetchAsset(id, trimmed)
      const url = await blobToDataUrl(blob, blob.type)

      const next = new Map(assetUrls.value)
      next.set(trimmed, url)
      assetUrls.value = next

      return url
    } catch {
      return null
    }
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[badgeTemplate] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    template.value = null
    loading.value = false
    error.value = null
    ready.value = false
    assetUrls.value = new Map()
  }

  return {
    // state
    eventId,
    template,
    loading,
    error,
    ready,
    assetUrls,
    // getters
    hasTemplate,
    isEmpty,
    // actions
    load,
    save,
    adopt,
    remove,
    uploadAsset,
    assetUrl,
    $reset,
  }
})

/**
 * Blob → `data:`-URL.
 *
 * `FileReader`, а не `URL.createObjectURL`, по причине из шапки стора: эти
 * байты уезжают в документ печати, а `blob:`-ссылка, созданная в другом
 * документе, в задании печати рисуется не всегда — Safari по этому пути
 * регулярно печатает пустое место. data-URL самодостаточен и не требует
 * освобождения, за что платит третью размера в памяти.
 *
 * `contentType` берётся у сервера, а не у блоба: у ответа axios с
 * `responseType: 'blob'` тип обычно есть, но у только что выбранного в
 * `<input type="file">` файла он приходит от ОС и бывает пустым.
 */
async function blobToDataUrl(blob: Blob, contentType: string): Promise<string> {
  const typed = contentType && blob.type !== contentType
    ? new Blob([await blob.arrayBuffer()], { type: contentType })
    : blob

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('не удалось прочитать файл'))
    reader.readAsDataURL(typed)
  })
}
